/**
 * useReferralTracking — Captures ?ref= query param on first visit.
 *
 * Fires `referral_link_clicked` PostHog event and persists the
 * referral code in localStorage so it can be attached to checkout.
 *
 * Runs once per session (idempotent via sessionStorage guard).
 */

import { useEffect } from "react";
import { useLocation } from "react-router";
import { track } from "@/lib/analytics";

const STORAGE_KEY = "fp_referral_code";
const SESSION_KEY = "fp_referral_tracked";
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function normalizeReferralCode(value: string | null | undefined): string | null {
  const code = value?.trim();
  if (!code || !REFERRAL_CODE_PATTERN.test(code)) return null;
  return code;
}

export function useReferralTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = normalizeReferralCode(params.get("ref"));

    // A partner page can navigate to /check inside the SPA. Track each new
    // referral code once per session, rather than only the initial document URL.
    if (ref && sessionStorage.getItem(SESSION_KEY) !== ref) {
      // Persist for checkout attribution
      localStorage.setItem(STORAGE_KEY, ref);

      // PostHog event
      track("referral_link_clicked", {
        referral_code: ref,
        landing_url: window.location.pathname,
      });

      sessionStorage.setItem(SESSION_KEY, ref);
    }
  }, [location.search, location.pathname]);
}

/** Read the stored referral code (for checkout attribution). */
export function getReferralCode(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
